import React, { useContext, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { GeneratedQuizQuestionDto } from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { AppContext } from '../app/AppProvider.component';
import { trackNewRelicAction } from '../app/app-state-actions.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { MARKDOWN_REHYPE_PLUGINS, MARKDOWN_REMARK_PLUGINS } from '../../utilities/common.utilities';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';

export type QuizQuestionListProps = {
  questions: GeneratedQuizQuestionDto[];
};

const QuizQuestionList = (props: QuizQuestionListProps): JSX.Element => {

  const {
    appState,
    appStateRef
  } = useContext(AppContext);

  // Keyed by question id (falling back to index only for the rare item
  // missing one) rather than array position, so expansion state cannot
  // apply to the wrong question if the list is ever re-sorted or refetched.
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const sortedQuestions = [...(props.questions ?? [])]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  const handleToggle = (questionKey: string) => {
    const isExpanding = !expandedIds[questionKey];

    if (isExpanding) {
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_VIEW_ALL_EXPAND_CLICK,
          props: {
            chatUserRole: appState.chatUserRole
          }
        }
      });
    }

    setExpandedIds((prevState) => ({
      ...prevState,
      [questionKey]: isExpanding
    }));
  };

  return (
    <div className="c-naa-panel__quiz-list">
      {sortedQuestions.map((question, index) => {
        // The two key sources are namespaced so they cannot collide: a bare
        // `id ?? index` lets a question with id 1 and an id-less question at
        // index 1 produce the same key, which makes React reuse the node and
        // makes both questions share one expansion state and one DOM id.
        const questionKey = question.id != null ? `id-${question.id}` : `index-${index}`;
        const qtiData = question.questionData?.qtiData;
        const responseChoices = qtiData?.responseChoices ?? {};
        const feedback = qtiData?.feedback ?? {};
        const correctResponse = qtiData?.correctResponse ?? [];
        // Sorted choiceId order + A/B/C... lettering matches
        // formatQuizQuestionsForCopy (common.utilities.ts), so letters agree
        // between this list and the copy-all output.
        const choiceIds = Object.keys(responseChoices).sort();
        const getChoiceLetter = (choiceId: string): string => String.fromCharCode(65 + choiceIds.indexOf(choiceId));
        const correctLetters = choiceIds
          .filter((choiceId) => correctResponse.includes(choiceId))
          .map(getChoiceLetter);
        const rationaleChoiceIds = choiceIds.filter((choiceId) => !!feedback[choiceId]);
        const isExpanded = !!expandedIds[questionKey];
        const answerId = `quiz-question-answer-${questionKey}`;

        return (
          <div key={questionKey} className="c-naa-panel__quiz-question">
            <h4 className="c-naa-panel__quiz-question-heading">{`Question ${index + 1}`}</h4>
            <div className="c-naa-panel__quiz-prompt">
              <ReactMarkdown
                remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
              >
                {qtiData?.prompt ?? ''}
              </ReactMarkdown>
            </div>
            <ul className="c-naa-panel__quiz-choices">
              {choiceIds.map((choiceId) => (
                <li key={choiceId} className="c-naa-panel__quiz-choice-item">
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_1o2]}>
                    <FlexItem>
                      <span className="u-els-font-family-bold">{`${getChoiceLetter(choiceId)}.`}</span>
                    </FlexItem>
                    <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                      <ReactMarkdown
                        remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                        rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
                      >
                        {responseChoices[choiceId]}
                      </ReactMarkdown>
                    </FlexItem>
                  </FlexLayout>
                </li>
              ))}
            </ul>
            <div className="c-naa-panel__quiz-answer-toggle">
              <button
                type="button"
                className="u-els-anchorize"
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? answerId : undefined}
                onClick={() => handleToggle(questionKey)}
              >
                {isExpanded ? 'Hide answer' : 'Show answer'}
                <span className="u-els-hide-visually">{` for question ${index + 1}`}</span>
              </button>
            </div>

            {isExpanded && (
              <div id={answerId} className="c-naa-panel__quiz-answer">
                {!!correctLetters.length && (
                  <>
                    <div className="u-els-font-family-bold">Correct answer</div>
                    <div>{correctLetters.join(', ')}</div>
                  </>
                )}
                {!!rationaleChoiceIds.length && (
                  <div className="u-els-font-family-bold u-els-margin-top-1o2">Rationale</div>
                )}
                <ul className="c-naa-panel__quiz-choices">
                  {rationaleChoiceIds
                    .map((choiceId) => (
                      <li key={choiceId} className="c-naa-panel__quiz-choice-item">
                        <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_1o2]}>
                          <FlexItem>
                            <span className="u-els-font-family-bold">{`${getChoiceLetter(choiceId)}.`}</span>
                          </FlexItem>
                          <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                            <ReactMarkdown
                              remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                              rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
                            >
                              {feedback[choiceId]}
                            </ReactMarkdown>
                          </FlexItem>
                        </FlexLayout>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default QuizQuestionList;
